import COPY from "./content/siteCopy.json";
import React from "react";
import DOMPurify from "dompurify";
import { SmartLink } from "./SiteHeader";
import { ChapterCarousel } from "./ChapterCarousel";

const clean = (h) => ({ __html: DOMPurify.sanitize(h || "", { FORBID_TAGS: ["script", "style", "iframe"] }) });
const pad = (n) => String(n).padStart(2, "0");

function ChapterHead({ n, title, lead, label }) {
  return (
    <div className="intro chapter-head">
      <div><div className="eyebrow" data-testid={`solution-chapter-label-${n}`}>{label || `Chapter ${pad(n)}`}</div><h2 className="display">{title}</h2></div>
      <div className="chapter-lead">{lead.map((h, i) => <p key={i} dangerouslySetInnerHTML={clean(h)} />)}</div>
    </div>
  );
}

function Outro({ html }) {
  return html.length ? <div className="chapter-outro">{html.map((h, i) => <p key={i} dangerouslySetInnerHTML={clean(h)} />)}</div> : null;
}

// One legacy h2 section → a visually distinct chapter (cards / steps / guides / prose).
export function SolutionChapter({ section, n, label, guidesLead = COPY.solution.guides_lead_default, carousel = false, carouselVariant = "classic" }) {
  const { kind, title, lead, cards, guides, outro } = section;
  const tone = n % 2 ? "chapter alt" : "chapter";
  if (kind === "guides") {
    return (
      <section className={tone} data-testid={`solution-chapter-${n}`}>
        <div className="wrap">
          <div className="intro chapter-head"><div><div className="eyebrow" data-testid={`solution-chapter-label-${n}`}>{label || `Chapter ${pad(n)}`}</div><h2 className="display">{title}</h2></div><p className="chapter-lead">{guidesLead}</p></div>
          <div className="guide-grid">
            {guides.map((g) => (
              <SmartLink className="guide-card" href={g.href || "/strategy-vault/"} key={g.title} data-testid="solution-guide-card">
                {g.img && <img src={g.img} alt={g.imgAlt || g.title} loading="lazy" />}
                <div><h3>{g.title}</h3><p dangerouslySetInnerHTML={clean(g.text)} /><span className="tool-link">{g.cta && !/^learn more$/i.test(g.cta) ? `${g.cta} →` : "Learn more →"}</span></div>
              </SmartLink>
            ))}
          </div>
        </div>
      </section>
    );
  }
  if (kind === "steps") {
    return (
      <section className={tone} data-testid={`solution-chapter-${n}`}>
        <div className="wrap">
          <ChapterHead n={n} title={title} lead={lead} label={label} />
          <ol className="xp-timeline chapter-steps" data-testid="solution-chapter-steps">
            {section.steps.map((st, i) => (
              <li className="xp-tl-step" key={i}>
                <div className="xp-tl-rail"><span className="xp-tl-n">{i + 1}</span></div>
                <div className="xp-tl-body">{st.title && <h3>{st.title.replace(/:$/, "")}</h3>}<p dangerouslySetInnerHTML={clean(st.text)} /></div>
              </li>
            ))}
          </ol>
          <Outro html={outro} />
        </div>
      </section>
    );
  }
  if (kind === "cards" && carousel) {
    return (
      <section className={`${tone} chapter-social`} data-testid={`solution-chapter-${n}`}>
        <div className="wrap">
          <ChapterHead n={n} title={title} lead={lead} label={label} />
          <ChapterCarousel cards={cards} title={title} label={label || `Chapter ${pad(n)}`} n={n} variant={carouselVariant} />
          <Outro html={outro} />
        </div>
      </section>
    );
  }
  if (kind === "cards") {
    return (
      <section className={tone} data-testid={`solution-chapter-${n}`}>
        <div className="wrap">
          <ChapterHead n={n} title={title} lead={lead} label={label} />
          <div className={`chapter-cards cols-${cards.length === 5 || cards.length === 6 ? 3 : Math.min(cards.length, 4)}${cards.some((c) => c.icon) ? " has-icons" : ""}`}>
            {cards.map((c, i) => (
              <div className="chapter-card" key={c.title} data-testid="solution-program-card">
                {c.icon && <img className="chapter-card-icon" src={c.icon} alt="" loading="lazy" />}
                <span className="num">{pad(i + 1)}</span><h3>{c.title}</h3>
                {c.texts.map((t, j) => <p key={j} dangerouslySetInnerHTML={clean(t)} />)}
                {c.steps.length > 0 && <ol className="chapter-card-steps">{c.steps.map((st, j) => <li key={j}>{st.title && <strong>{st.title}</strong>}<span dangerouslySetInnerHTML={clean(st.text)} /></li>)}</ol>}
                {c.bullets.length > 0 && <ul>{c.bullets.map((b, j) => <li key={j} dangerouslySetInnerHTML={clean(b)} />)}</ul>}
              </div>
            ))}
          </div>
          <Outro html={outro} />
        </div>
      </section>
    );
  }
  return (
    <section className={tone} data-testid={`solution-chapter-${n}`}>
      <div className="wrap"><ChapterHead n={n} title={title} label={label} lead={[...lead, ...outro, ...cards.flatMap((c) => [`<strong>${c.title}</strong>`, ...c.texts, ...(c.bullets.length ? [`<ul>${c.bullets.map((b) => `<li>${b}</li>`).join("")}</ul>`] : [])])]} /></div>
    </section>
  );
}
